import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { humanizeAccountType, humanizeIndustry } from '../../lib/accountAnalytics';
import type { DraftMove } from '../../lib/accountAnalytics';
import type { AccountAnalyticsRow } from '../../types/accountAnalytics';

// Lean analytics table: name, type, brand, group, industry, AM, status. Client-
// side sort + paginate over the already-filtered rows. Multi-select checkboxes
// feed the Phase 2 reassignment draft; drafted rows show their staged AM
// (current → staged) with a remove-from-draft control.

type SortField = 'name' | 'accountType' | 'brandName' | 'group' | 'industry' | 'accountManagerName' | 'status';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

const COLUMNS: { field: SortField; label: string }[] = [
  { field: 'name', label: 'Name' },
  { field: 'accountType', label: 'Type' },
  { field: 'brandName', label: 'Brand' },
  { field: 'group', label: 'Group' },
  { field: 'industry', label: 'Industry' },
  { field: 'accountManagerName', label: 'AM' },
  { field: 'status', label: 'Status' },
];

function cellValue(row: AccountAnalyticsRow, field: SortField): string {
  switch (field) {
    case 'name':
      return row.name;
    case 'accountType':
      return humanizeAccountType(row.accountType);
    case 'brandName':
      return row.brandName ?? '';
    case 'group':
      return row.group ?? '';
    case 'industry':
      return humanizeIndustry(row.industry);
    case 'accountManagerName':
      return row.accountManagerName ?? '';
    case 'status':
      return row.status;
  }
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 ring-green-600/20',
  paused: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  cancelled: 'bg-slate-100 text-slate-500 ring-slate-500/20',
};

export function AnalyticsTable({
  rows,
  selected,
  onToggleRow,
  onTogglePage,
  defaultSort,
  moveByAccountId,
  onRemoveFromDraft,
}: {
  rows: AccountAnalyticsRow[];
  selected: Set<string>;
  onToggleRow: (id: string) => void;
  // Toggle every id in the array on/off together (select-all-on-page).
  onTogglePage: (ids: string[], select: boolean) => void;
  defaultSort: SortField;
  // Effective draft moves keyed by account id (drives the current → staged AM
  // cell + the remove control). Empty/absent when not drafting.
  moveByAccountId?: Map<string, DraftMove>;
  onRemoveFromDraft?: (id: string) => void;
}) {
  const [sortField, setSortField] = useState<SortField>(defaultSort);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = cellValue(a, sortField);
      const bv = cellValue(b, sortField);
      // Blanks last regardless of direction.
      if (av === '' && bv !== '') return 1;
      if (bv === '' && av !== '') return -1;
      const c = av.localeCompare(bv);
      return (c !== 0 ? c : a.name.localeCompare(b.name)) * mul;
    });
  }, [rows, sortField, sortDir]);

  const total = sorted.length;
  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clampedPage = Math.min(page, maxPage);
  const start = (clampedPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(start, start + PAGE_SIZE);
  const pageIds = pageRows.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleSort = (field: SortField) => {
    setPage(1);
    if (field === sortField) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const arrow = (field: SortField) =>
    field === sortField ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium text-slate-500">
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all on page"
                  checked={allPageSelected}
                  onChange={(e) => onTogglePage(pageIds, e.target.checked)}
                />
              </th>
              {COLUMNS.map((c) => (
                <th
                  key={c.field}
                  className="cursor-pointer select-none whitespace-nowrap px-3 py-2 hover:text-slate-800"
                  onClick={() => toggleSort(c.field)}
                >
                  {c.label}
                  {arrow(c.field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-3 py-8 text-center text-slate-400">
                  No accounts match the current filters.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                const move = moveByAccountId?.get(row.id);
                return (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b border-slate-100 last:border-0 hover:bg-slate-50',
                    move ? 'bg-amber-50/60' : selected.has(row.id) && 'bg-kyck-50/50',
                  )}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.name}`}
                      checked={selected.has(row.id)}
                      onChange={() => onToggleRow(row.id)}
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">
                    <Link to={`/accounts/${row.id}`} className="hover:text-kyck-700 hover:underline">
                      {row.name}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 capitalize text-slate-600">
                    {humanizeAccountType(row.accountType)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">{row.brandName ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">{row.group ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 capitalize text-slate-600">
                    {humanizeIndustry(row.industry)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {move ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-slate-400 line-through">{move.fromAmLabel}</span>
                        <span className="text-slate-400">→</span>
                        <span className="font-medium text-kyck-700">{move.toAmLabel}</span>
                        {onRemoveFromDraft && (
                          <button
                            type="button"
                            aria-label={`Remove ${row.name} from draft`}
                            title="Remove from draft"
                            onClick={() => onRemoveFromDraft(row.id)}
                            className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    ) : (
                      row.accountManagerName ?? <span className="text-slate-400">Unassigned</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset',
                        STATUS_STYLES[row.status] ?? 'bg-slate-100 text-slate-600 ring-slate-500/20',
                      )}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer: count + pagination */}
      <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
        <span>
          {total.toLocaleString()} account{total === 1 ? '' : 's'}
          {selected.size > 0 && ` · ${selected.size} selected`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={clampedPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            Page {clampedPage} / {maxPage}
          </span>
          <button
            type="button"
            disabled={clampedPage >= maxPage}
            onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
            className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
